import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const GAP = 8;
const EDGE = 8;

/**
 * The app's hover tooltip. Wraps whatever it is given and shows `text` above
 * it, or below when there is no room above.
 *
 * The bubble is portalled to `document.body` and positioned `fixed`, the same
 * way `ui/Select` places its list. It used to be an `absolute` box that lived
 * in the row, hidden with `opacity-0 invisible` — which hides a box but keeps
 * it in layout, so every tooltip still counted towards its scroll container's
 * scrollable width. On the SuperAdmins table that put ~20px of nothing to the
 * right of the last column and left the table with a permanent horizontal
 * scrollbar that scrolled to blank space. Inside `overflow-x-auto` the bubble
 * was also clipped on the top row, because a scroller cannot be `visible` on
 * one axis alone.
 *
 * Hover lives on the wrapper, not on the child, so a disabled button still
 * explains why it is disabled — that is most of what this is used for.
 */
const CustomTooltip = ({ text, children }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const wrapperRef = useRef(null);
  const bubbleRef = useRef(null);

  // Measure once the bubble is in the DOM: its own width decides both the
  // centring and the clamp that keeps it on screen.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = wrapperRef.current?.getBoundingClientRect();
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!trigger || !bubble) return;

    const below = trigger.top - GAP - bubble.height < EDGE;
    const left = Math.min(
      Math.max(trigger.left + trigger.width / 2 - bubble.width / 2, EDGE),
      window.innerWidth - bubble.width - EDGE,
    );

    setStyle({
      position: "fixed",
      left,
      top: below ? trigger.bottom + GAP : trigger.top - GAP - bubble.height,
      zIndex: 9999,
    });
  }, [open, text]);

  // A fixed bubble does not travel with the row under it, so it goes away
  // rather than hanging over the wrong control.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}

      {open &&
        text &&
        createPortal(
          <div
            ref={bubbleRef}
            role="tooltip"
            // `pointer-events-none` so the bubble never sits between the
            // pointer and the control it describes.
            className="pointer-events-none max-w-xs rounded-lg border border-border-primary bg-background-primary px-3 py-1.5 text-xs font-medium text-text-primary shadow-lg"
            style={{ position: "fixed", top: -9999, left: -9999, zIndex: 9999, ...style }}
          >
            {text}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CustomTooltip;
